import { useContext, useEffect } from "react";
import { GrowthBookContext } from "@growthbook/growthbook-react";
import Auth from "../Auth";
import {
  DEFAULT_AGENTIC_CHAT_MODE,
  TRIAL_DEFAULT_AGENTIC_CHAT_MODE_COHORT_KEY,
  TRIAL_DEFAULT_AGENTIC_CHAT_MODE_EXPERIMENT_KEY,
  isTrialDefaultAgenticChatModeCohort,
} from "../../../src/shared/constants";
import {
  GrowthBookAuthReadyContext,
  canEvaluateUserScopedGrowthBookExperiments,
} from "../lib/growthbookExperimentTracking";
import { useSudowriteStore } from "../stores/sudowriteStore";
import useUserMetadata from "./useUserMetadata";

export function resolveTrialDefaultAgenticChatMode({
  persistedCohort,
  growthbookMode,
  isTrial,
  fallback = DEFAULT_AGENTIC_CHAT_MODE.value,
}) {
  if (isTrialDefaultAgenticChatModeCohort(persistedCohort)) {
    return persistedCohort;
  }
  if (isTrial && isTrialDefaultAgenticChatModeCohort(growthbookMode)) {
    return growthbookMode;
  }
  return fallback;
}

/**
 * Trial-only GrowthBook assignment for default agentic chat composer mode.
 * Cohort is persisted on the user and survives subscribe.
 */
export default function useTrialDefaultAgenticChatModeExperiment() {
  const { growthbook } = useContext(GrowthBookContext) ?? {};
  const growthbookAuthReady = useContext(GrowthBookAuthReadyContext);
  const subscription = useSudowriteStore((state) => state.subscription);
  const userId = useSudowriteStore((state) => state.userData?.userId ?? null);
  const { userMetadata, refetch: refetchUserMetadata } = useUserMetadata();
  const isTrial = !!(subscription?.fetched && subscription.activeTrial());
  const persistedCohort = userMetadata?.experiments?.[TRIAL_DEFAULT_AGENTIC_CHAT_MODE_COHORT_KEY] ?? null;
  const canEvaluateExperiment =
    isTrial && canEvaluateUserScopedGrowthBookExperiments(growthbook, userId, growthbookAuthReady);
  const growthbookMode = canEvaluateExperiment
    ? growthbook?.getFeatureValue?.(
        TRIAL_DEFAULT_AGENTIC_CHAT_MODE_EXPERIMENT_KEY,
        DEFAULT_AGENTIC_CHAT_MODE.value,
      )
    : null;
  const defaultMode = resolveTrialDefaultAgenticChatMode({
    persistedCohort,
    growthbookMode,
    isTrial,
  });
  const shouldApplyDefault =
    isTrialDefaultAgenticChatModeCohort(persistedCohort) || canEvaluateExperiment;

  useEffect(() => {
    if (!canEvaluateExperiment || persistedCohort || !growthbook) {
      return;
    }

    const mode = growthbook.getFeatureValue(
      TRIAL_DEFAULT_AGENTIC_CHAT_MODE_EXPERIMENT_KEY,
      DEFAULT_AGENTIC_CHAT_MODE.value,
    );
    if (!isTrialDefaultAgenticChatModeCohort(mode)) {
      return;
    }

    Auth.fetchWithIdToken("/api/user-metadata/trial-default-agentic-chat-mode-cohort", { mode })
      .then(() => refetchUserMetadata())
      .catch((err) =>
        console.warn("[GrowthBook] failed to persist trial-default-agentic-chat-mode cohort", err),
      );
  }, [canEvaluateExperiment, persistedCohort, growthbook, refetchUserMetadata]);

  const userData = useSudowriteStore((state) => state.userData);
  const savedAgenticChatMode = userData?.data?.agenticChatMode?.value;

  useEffect(() => {
    if (savedAgenticChatMode != null || !shouldApplyDefault) {
      return;
    }
    userData?.update({ agenticChatMode: { value: defaultMode } });
  }, [savedAgenticChatMode, shouldApplyDefault, defaultMode, userData]);

  return {
    defaultMode,
    shouldApplyDefault,
  };
}
